import React, {
  ComponentPropsWithoutRef,
  Dispatch,
  forwardRef,
  Fragment,
  SetStateAction,
} from 'react'

import { useTranslations } from 'next-intl'
import { CaretDownIcon } from '@phosphor-icons/react/dist/ssr/CaretDown'
import { DropdownMenu } from '@skop-app/lib-front'
import { LabelType } from '@skop-app/lib-domain/types'
import { cx } from 'class-variance-authority'

type ReviewsFiltersProps = {
  projects?: LabelType[]
  filters: ReviewsFiltersType
  setFilters: Dispatch<SetStateAction<ReviewsFiltersType>>
}

export type ReviewsFiltersType = {
  activeTab: 'allComments' | 'forYou'
  activeStatus: 'open' | 'closed' | 'allStatuses'
  activeProject: string
}

type FilterTriggerProps = ComponentPropsWithoutRef<'button'> & {
  label: string
}

const FilterTrigger = forwardRef<HTMLButtonElement, FilterTriggerProps>(
  ({ label, className, type = 'button', ...props }, ref) => (
    <button
      ref={ref}
      type={type}
      className={cx(
        'flex w-full min-w-0 items-center justify-between gap-2 rounded-md border border-neutral-200/80 bg-white px-2 py-1.5 text-xs font-medium text-neutral-600 transition hover:border-neutral-300/90 hover:bg-neutral-50 hover:text-neutral-900 focus:outline-none focus:ring-2 focus:ring-neutral-200/80',
        className,
      )}
      {...props}
    >
      <span className="truncate">{label}</span>
      <CaretDownIcon className="size-4 shrink-0 text-neutral-400" />
    </button>
  ),
)

FilterTrigger.displayName = 'FilterTrigger'

export const ReviewsFilters = ({
  projects = [],
  filters: { activeTab, activeStatus, activeProject },
  setFilters,
}: ReviewsFiltersProps) => {
  const t = useTranslations()

  const tabs = [
    {
      key: 'allComments' as const,
      name: t('Review.allComments'),
    },
    {
      key: 'forYou' as const,
      name: t('Review.forYou'),
    },
  ]

  const statusMenu = [
    {
      key: 'allStatuses' as const,
      label: t('Review.allStatuses'),
    },
    {
      key: 'open' as const,
      label: t('Review.open'),
    },
    {
      key: 'closed' as const,
      label: t('Review.closed'),
    },
  ]

  const projectsOptions = [
    {
      value: 'allProjects',
      label: t('Review.allProjects'),
    },
    ...projects,
  ]

  const selectedProject = projectsOptions.find(
    (project) => project.value === activeProject,
  )?.label

  return (
    <div className="grid gap-1.5">
      <div className="inline-flex w-full rounded-md border border-neutral-200/70 bg-neutral-50 p-0.5">
        {tabs.map((tab) => {
          const isActive = tab.key === activeTab
          return (
            <button
              key={tab.key}
              type="button"
              onClick={() =>
                setFilters((prev) => ({
                  ...prev,
                  activeTab: tab.key,
                }))
              }
              className={cx(
                'w-1/2 rounded-md px-2.5 py-1.5 text-xs font-medium transition focus:outline-none focus:ring-2 focus:ring-neutral-200',
                isActive
                  ? 'bg-white text-slate-900 shadow-[0_1px_2px_rgba(15,23,42,0.04)]'
                  : 'text-slate-500 hover:bg-white/60 hover:text-slate-800',
              )}
            >
              {tab.name}
            </button>
          )
        })}
      </div>

      <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <FilterTrigger
              label={
                statusMenu.find((item) => item.key === activeStatus)?.label ||
                t('Review.allStatuses')
              }
            />
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content align="start">
              <DropdownMenu.Group>
                {statusMenu.map((item, index) => (
                  <Fragment key={item.key}>
                    <DropdownMenu.Item
                      onClick={() =>
                        setFilters((prev) => ({
                          ...prev,
                          activeStatus: item.key,
                        }))
                      }
                      className={cx(
                        '!py-2',
                        item.key === activeStatus &&
                          'font-semibold text-neutral-900',
                      )}
                    >
                      {item.label}
                    </DropdownMenu.Item>
                    {index < statusMenu.length - 1 && (
                      <DropdownMenu.Separator className="bg-neutral-200/80" />
                    )}
                  </Fragment>
                ))}
              </DropdownMenu.Group>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>

        {projects.length > 0 && (
          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
              <FilterTrigger
                label={selectedProject || t('Review.allProjects')}
                title={selectedProject}
              />
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
              <DropdownMenu.Content align="end">
                <DropdownMenu.Group>
                  {projectsOptions.map((project, index) => (
                    <Fragment key={project.value}>
                      <DropdownMenu.Item
                        onClick={() =>
                          setFilters((prev) => ({
                            ...prev,
                            activeProject: project.value as string,
                          }))
                        }
                        className={cx(
                          '!py-2',
                          project.value === activeProject &&
                            'font-semibold text-neutral-900',
                        )}
                      >
                        {project.label}
                      </DropdownMenu.Item>
                      {index < projectsOptions.length - 1 && (
                        <DropdownMenu.Separator className="bg-neutral-200/80" />
                      )}
                    </Fragment>
                  ))}
                </DropdownMenu.Group>
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
        )}
      </div>
    </div>
  )
}
